/**
 * <scheme-switcher>
 *
 * Lets a customer re-theme the closest [data-scheme-root] ancestor by clicking a
 * colour swatch.
 *
 * How it works, and why it is this small: snippets/css-variables.liquid emits a
 * .color-scheme_N class for EVERY scheme the merchant has defined, and each class
 * both declares the six scheme custom properties and paints itself. So switching
 * scheme is nothing more than swapping one class on one element — the background,
 * the text, the buttons and the scallop borders all follow from that, because
 * they read the custom properties rather than fixed colours.
 *
 * Nothing is persisted, by design. A reload returns to whatever the merchant set
 * in the theme editor, which also means there is no flash of the wrong scheme
 * before JavaScript runs.
 *
 * The border snippets' fill_color does NOT follow, and should not — it is the
 * adjoining section's background colour, and that section has not changed.
 */
if (!customElements.get("scheme-switcher")) {
  customElements.define(
    "scheme-switcher",
    class SchemeSwitcher extends HTMLElement {
      connectedCallback() {
        this.root = this.closest("[data-scheme-root]");
        if (!this.root) return;

        this.buttons = Array.from(this.querySelectorAll("[data-scheme-class]"));
        if (!this.buttons.length) return;

        // One delegated listener rather than one per swatch, so there is a single
        // thing to remove on disconnect.
        this.handleClick = this.handleClick.bind(this);
        this.addEventListener("click", this.handleClick);
      }

      disconnectedCallback() {
        if (this.handleClick) this.removeEventListener("click", this.handleClick);
      }

      handleClick(event) {
        const button = event.target.closest("[data-scheme-class]");
        if (!button || !this.contains(button)) return;

        this.applyScheme(button.dataset.schemeClass);

        this.buttons.forEach((candidate) => {
          candidate.setAttribute("aria-pressed", String(candidate === button));
        });
      }

      applyScheme(schemeClass) {
        if (!schemeClass) return;

        // Remove whichever colour class is currently on the root before adding the
        // new one. Matching on the prefix rather than a known list means this keeps
        // working if the merchant adds schemes in the theme editor.
        Array.from(this.root.classList)
          .filter((name) => name.startsWith("color-"))
          .forEach((name) => this.root.classList.remove(name));

        this.root.classList.add(schemeClass);
      }
    },
  );
}
